import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { migrationsApi } from '@/api/migrations';
import type { Migration } from '@/types/migration';
import { DataTable, Column } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Eye, Pencil, Trash2, Database, Check, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export function MigrationsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasRole } = useAuth();
  const [migrations, setMigrations] = useState<Migration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [deleteMigration, setDeleteMigration] = useState<Migration | null>(null);
  const [deleting, setDeleting] = useState(false);

  const isAdmin = hasRole('ROLE_ADMIN') || hasRole('ROLE_SUPERADMIN');

  // Get filters from URL params
  const filters = useMemo(
    () => ({
      search: searchParams.get('search') || '',
      page: Number(searchParams.get('page')) || 1,
      limit: Number(searchParams.get('limit')) || 25,
      sortBy: searchParams.get('sortBy') || 'id',
      sortOrder: (searchParams.get('sortOrder') as 'ASC' | 'DESC') || 'DESC',
    }),
    [searchParams]
  );

  // Update filters
  const updateFilters = useCallback(
    (newFilters: Partial<typeof filters>) => {
      const updated = { ...filters, ...newFilters };
      const params = new URLSearchParams();

      if (updated.search) params.set('search', updated.search);
      if (updated.page && updated.page !== 1) params.set('page', updated.page.toString());
      if (updated.limit && updated.limit !== 25) params.set('limit', updated.limit.toString());
      if (updated.sortBy && updated.sortBy !== 'id') params.set('sortBy', updated.sortBy);
      if (updated.sortOrder && updated.sortOrder !== 'DESC') params.set('sortOrder', updated.sortOrder);

      setSearchParams(params);
    },
    [filters, setSearchParams]
  );

  // Fetch data when filters change
  useEffect(() => {
    const fetchMigrations = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await migrationsApi.getAll({
          search: filters.search || undefined,
          page: filters.page,
          limit: filters.limit,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
        });
        setMigrations(response.data);
        setTotalItems(response.meta.total);
        setTotalPages(response.meta.totalPages);
      } catch (err) {
        setError('Erreur lors du chargement des migrations');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchMigrations();
  }, [filters]);

  const handleSort = (key: string) => {
    const newOrder = filters.sortBy === key && filters.sortOrder === 'ASC' ? 'DESC' : 'ASC';
    updateFilters({ sortBy: key, sortOrder: newOrder, page: 1 });
  };

  // Search with debounce
  const [searchInput, setSearchInput] = useState(filters.search);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        updateFilters({ search: searchInput, page: 1 });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput, filters.search, updateFilters]);

  const handleDelete = async () => {
    if (!deleteMigration) return;

    try {
      setDeleting(true);
      await migrationsApi.delete(deleteMigration.id);
      // Refresh data after deletion
      setTotalItems((prev) => prev - 1);
      setMigrations(migrations.filter((m) => m.id !== deleteMigration.id));
      setDeleteMigration(null);
    } catch (err) {
      console.error(err);
      setError('Erreur lors de la suppression de la migration');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getExecutionStatus = (migration: Migration) => {
    if (migration.passed) {
      return (
        <Badge variant="success" className="gap-1">
          <Check className="h-3 w-3" />
          Executee
        </Badge>
      );
    }
    if (migration.executedSchoolsCount > 0) {
      return (
        <Badge variant="warning" className="gap-1">
          Partielle ({migration.executedSchoolsCount}/{migration.totalSchoolsCount})
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="gap-1">
        <X className="h-3 w-3" />
        Non executee
      </Badge>
    );
  };

  const columns: Column<Migration>[] = [
    {
      key: 'id',
      header: 'ID',
      sortable: true,
      className: 'w-16',
      render: (migration) => (
        <span className="font-mono text-sm text-muted-foreground">
          {migration.id}
        </span>
      ),
    },
    {
      key: 'version',
      header: 'Release',
      sortable: true,
      className: 'w-24',
      render: (migration) => (
        <Badge variant="outline" className="font-mono">
          {migration.version}
        </Badge>
      ),
    },
    {
      key: 'name',
      header: 'Nom',
      sortable: true,
      render: (migration) => (
        <span className="font-medium text-gray-900">{migration.name}</span>
      ),
    },
    {
      key: 'passed',
      header: 'Statut',
      sortable: true,
      className: 'w-36',
      render: (migration) => getExecutionStatus(migration),
    },
    {
      key: 'passedAt',
      header: "Date d'execution",
      sortable: true,
      className: 'w-40',
      render: (migration) => (
        <span className="text-sm text-muted-foreground">
          {formatDate(migration.passedAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-right w-28',
      render: (migration) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => navigate(`/migrations/${migration.id}`)}
            title="Voir"
          >
            <Eye className="h-4 w-4" />
          </Button>
          {!migration.passed && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => navigate(`/migrations/${migration.id}/edit`)}
              title="Modifier"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setDeleteMigration(migration)}
              title="Supprimer"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Database className="h-5 w-5" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
              Migrations
            </h1>
          </div>
          <p className="text-sm md:text-base text-muted-foreground pl-[52px]">
            Gérez les scripts de migration SQL pour les écoles
          </p>
        </div>

        {isAdmin && (
          <Button asChild>
            <Link to="/migrations/new">
              <Plus className="h-4 w-4" />
              Nouvelle migration
            </Link>
          </Button>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={migrations}
        loading={loading}
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder="Rechercher par nom ou version..."
        sortBy={filters.sortBy}
        sortOrder={filters.sortOrder}
        onSort={handleSort}
        page={filters.page}
        pageSize={filters.limit}
        totalItems={totalItems}
        totalPages={totalPages}
        onPageChange={(page) => updateFilters({ page })}
        onPageSizeChange={(limit) => updateFilters({ limit, page: 1 })}
        emptyMessage="Aucune migration trouvee"
      />

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteMigration} onOpenChange={() => setDeleteMigration(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la migration ?</AlertDialogTitle>
            <AlertDialogDescription>
              Etes-vous sur de vouloir supprimer la migration{' '}
              <strong>{deleteMigration?.name}</strong> (v{deleteMigration?.version}) ?
              <br />
              <br />
              Cette action est irreversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
